import type {ChangeEvent} from 'react'
import React, {useState, useEffect, useCallback, useRef, Fragment} from 'react'
import {Combobox, ComboboxInput, ComboboxOption, ComboboxOptions, Label, Transition} from '@headlessui/react'
import {useFetcher, useNavigate, useRouteLoaderData} from '@remix-run/react'
import clsx from 'clsx'
import {getConfiguration} from '~/utils/configuration/configuration'

import type {RootLoaderResponse, SearchCategoriesResponseProps} from '~/types'
import {getTranslation} from '~/localization/translation'
import {
  useReportCustomEvent,
  useReportItemSelected,
  useReportSendSearch,
  useReportWidgetClick,
} from '~/hooks/analytic-hooks'
import {BrgIconSearch} from '~/icons/brg-icon-search'
import {IconArrowRight} from '~/icons/icon-arrow-right'
import {BrgIconSearchPlaceholder} from '~/icons/brg-icon-search-placeholder'
import {getDomainConfig} from '~/domains'
import DropdownOption from './search-box-dropdown-option'
import DropdownOptionSkeleton from './search-box-dropdown-option-skeleton'
import SearchBoxDropdownError from './search-box-dropdown-error'
import SearchBoxDropdownEmpty from './search-box-dropdown-empty'

const SEARCH_QUERY_MAX_LENGTH = 30
const SEARCH_QUERY_MIN_LENGTH = 2
const SEARCH_QUERY_DEBOUNCE_MS = 350

const SearchBoxRedesign = ({
  standalone,
  isSaleEventInfo,
  fullWidth = true,
}: {
  standalone?: boolean
  isSaleEventInfo?: boolean
  fullWidth?: boolean
}) => {
  const {locale, language, deviceType, featureFlags, domain, forcedLanguage} = useRouteLoaderData(
    'root',
  ) as RootLoaderResponse

  const fetcher = useFetcher<SearchCategoriesResponseProps>()
  const reportSearch = useReportSendSearch()
  const reportWidgetClick = useReportWidgetClick()
  const reportItemSelected = useReportItemSelected()
  const reportCustomEvent = useReportCustomEvent()
  const topCategories = getConfiguration(locale)
  const fetcherCategoriesData = fetcher?.data as SearchCategoriesResponseProps
  const translations = getTranslation(language)
  const {styles: stylesBydomain} = getDomainConfig(domain, locale)

  const comboboxFormRef = useRef<HTMLFormElement>(null)
  const comboButtonRef = useRef<HTMLButtonElement>(null)
  const comboOptionsRef = useRef<HTMLUListElement>(null)
  const comboboxInputRef = useRef<HTMLInputElement>(null)
  const searchDebounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendingSearchTermRef = useRef<string | null>(null)
  const previousFetcherStateRef = useRef(fetcher.state)

  const [reportedHoverSearchPanelOnce, setReportedHoverSearchPanelOnce] = useState(false)
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('')
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [isSearchPending, setIsSearchPending] = useState(false)
  const navigate = useNavigate()
  const mostRelevantCategories = !fetcher?.data || !query.length ? topCategories : fetcherCategoriesData

  const showPrimeDayAppbar = Boolean(featureFlags?.enablePrimeDayAppbar) || isSaleEventInfo
  const searchIconStyle = showPrimeDayAppbar
    ? 'fill-white'
    : `${stylesBydomain.COMMONS_WIDGETS_SEARCH_ICON} commons_widgets_search_icon`

  useEffect(() => {
    const justSettled = previousFetcherStateRef.current !== 'idle' && fetcher.state === 'idle'
    if (justSettled && pendingSearchTermRef.current) {
      const completedQuery = fetcher.data?.query?.trim()
      if (completedQuery && completedQuery === pendingSearchTermRef.current) {
        reportSearch({searchTerm: completedQuery})
      }
      pendingSearchTermRef.current = null
    }
    previousFetcherStateRef.current = fetcher.state
  }, [fetcher.state, fetcher.data, reportSearch])

  const handleOutsideClick = (e: any) => {
    if (comboboxFormRef.current && !comboboxFormRef.current.contains(e.target)) {
      if (comboButtonRef.current && comboOptionsRef.current) {
        comboButtonRef.current.click()
      }
    }
  }

  useEffect(() => {
    document.addEventListener('mousedown', handleOutsideClick)
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
    }
  })

  useEffect(() => {
    if (open && comboboxInputRef.current) {
      comboboxInputRef.current.focus()
    }
  }, [open, fetcher.state])

  useEffect(() => {
    if (searchDebounceTimerRef.current) {
      clearTimeout(searchDebounceTimerRef.current)
      searchDebounceTimerRef.current = null
    }

    const normalizedQuery = query.trim()
    if (normalizedQuery.length < SEARCH_QUERY_MIN_LENGTH) {
      pendingSearchTermRef.current = null
      setIsSearchPending(false)
      return
    }

    setIsSearchPending(true)
    searchDebounceTimerRef.current = setTimeout(() => {
      setIsSearchPending(false)
      pendingSearchTermRef.current = normalizedQuery
      fetcher.load(`/categories-search?q=${encodeURIComponent(normalizedQuery)}&limit=5`)
    }, SEARCH_QUERY_DEBOUNCE_MS)

    return () => {
      if (searchDebounceTimerRef.current) {
        clearTimeout(searchDebounceTimerRef.current)
        searchDebounceTimerRef.current = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps, @eslint-react/exhaustive-deps
  }, [query])

  const renderCategories = useCallback(
    (data?: SearchCategoriesResponseProps) => {
      const {categories} = data || {}

      if (isSearchPending || fetcher.state === 'loading') {
        return (
          <div>
            <Label>
              <div className="mb-6 ml-10 h-3 max-w-[15%] animate-pulse-fast rounded-sm bg-gray-200 pl-10"></div>
            </Label>
            {mostRelevantCategories?.categories?.map((category, i) => (
              <ComboboxOption
                key={`${category.displayName}-${i}`}
                className="relative select-none list-none py-2 pl-10 pr-4"
                value={category.displayName}
              >
                <DropdownOptionSkeleton />
              </ComboboxOption>
            ))}
          </div>
        )
      }
      if (data?.isError) {
        return <SearchBoxDropdownError />
      }
      if (categories && categories.length === 0) {
        return <SearchBoxDropdownEmpty />
      }
      return (
        <div>
          <Label>
            <p className="mb-6 pl-10 text-sm font-semibold text-black">
              {translations.CategorySearchCombo.mostRelevant}
            </p>
          </Label>
          {categories?.map((category, i) => (
            <ComboboxOption
              key={`${category.displayName}-${i}`}
              className="relative my-1 cursor-default select-none list-none pl-10 pr-4 ui-active:bg-gray-100"
              value={forcedLanguage ? `${forcedLanguage}/${category.slug}` : category.slug}
            >
              <DropdownOption category={category} query={query} />
            </ComboboxOption>
          ))}
        </div>
      )
    },
    [
      isSearchPending,
      fetcher.state,
      translations.CategorySearchCombo,
      query,
      mostRelevantCategories?.categories,
      forcedLanguage,
    ],
  )

  const handleSelectCategory = (category: string | null) => {
    if (!category || !category.length) return
    const categoryIndex = mostRelevantCategories?.categories?.findIndex((item) => item.slug === category) || 0
    const categoryDisplayName = mostRelevantCategories?.categories?.[categoryIndex]?.displayName || ''
    setSelectedCategory(`${categoryDisplayName} ...`)
    reportItemSelected({
      index: categoryIndex,
      type: 'item',
      listId: category,
    })
    fetcher.submit({category}, {method: 'POST', action: '/categories-search'})
    if (comboboxInputRef.current) comboboxInputRef.current.disabled = true
    setQuery('')
  }
  const handleEnterSearchTerm = (query: string) => {
    const normalizedQuery = query.trim()
    if (normalizedQuery.length < SEARCH_QUERY_MIN_LENGTH) return

    if (comboButtonRef.current && !comboOptionsRef.current) {
      comboButtonRef.current.click()
    }
    reportWidgetClick({
      widget_name: 'search-panel',
      widget_place: 'app-bar',
      trigger_type: 'input-field-enter',
      variation: normalizedQuery,
    })

    navigate(`/categories-search?q=${encodeURIComponent(normalizedQuery)}`)
    if (comboboxInputRef.current) comboboxInputRef.current.disabled = true
    setQuery('')
  }

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextQuery = event.target.value.slice(0, SEARCH_QUERY_MAX_LENGTH)
    setIsDropdownOpen(nextQuery.trim().length >= SEARCH_QUERY_MIN_LENGTH)
    setQuery(nextQuery)
  }

  return (
    <div
      className={clsx(
        'z-40 flex flex-row justify-between',
        open && `${stylesBydomain.COMMONS_APPBAR_BG} commons_appbar_bg`,
        {
          'tablet:absolute tablet:left-0 tablet:w-full': open,
          'tablet:top-3': open && !featureFlags?.showNewHeaderAndCookieUi,
          'tablet:top-1': open && featureFlags?.showNewHeaderAndCookieUi,
        },
      )}
      key={locale}
    >
      <div
        className={clsx(
          fullWidth && 'w-full',
          open
            ? 'mx-3 flex flex-row justify-center rounded-full bg-white px-4 py-2 desktop:mx-0'
            : {
                'relative flex flex-row justify-center rounded-full bg-white px-4 py-2': standalone,
                'relative mx-3 flex flex-row justify-center rounded-full bg-white px-4 py-2 desktop:mx-0 tablet:hidden':
                  !standalone,
              },
          stylesBydomain.COMMONS_WIDGETS_SEARCH_SHADOW,
          'commons_widgets_search_shadow',
        )}
      >
        <fetcher.Form ref={comboboxFormRef} className="w-full text-base font-normal outline-none" autoComplete="off">
          <Combobox value={selectedCategory} onChange={handleSelectCategory} onClose={() => setIsDropdownOpen(false)}>
            <div>
              <div className="relative w-full">
                <ComboboxInput
                  className="w-full px-5 text-base font-normal text-black outline-none placeholder:text-sm placeholder:text-neutral-500 disabled:bg-inherit disabled:text-gray-400"
                  ref={comboboxInputRef}
                  type="text"
                  autoComplete="off"
                  required
                  name="search-query"
                  maxLength={SEARCH_QUERY_MAX_LENGTH}
                  data-testid="search-input"
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && featureFlags?.useNewSearchPage) {
                      event.preventDefault()
                      handleEnterSearchTerm(query)
                    }
                  }}
                  onMouseEnter={() => {
                    if (!reportedHoverSearchPanelOnce && deviceType === 'desktop') {
                      reportCustomEvent('hover', {
                        elementName: 'search-panel',
                        placement: 'app-bar',
                        variation: 'above-the-list-redesign',
                      })
                      setReportedHoverSearchPanelOnce(true)
                    }
                  }}
                  onClick={() => {
                    if (comboButtonRef.current && !comboOptionsRef.current) {
                      comboButtonRef.current.click()
                    }
                    reportWidgetClick({
                      widget_name: 'search-panel',
                      widget_place: 'app-bar',
                      trigger_type: 'input-field',
                    })
                  }}
                  onChange={handleInputChange}
                  placeholder={translations.CategorySearchCombo.placeholderAboveTheListRedesign}
                />
                <BrgIconSearchPlaceholder className="absolute -left-1.5 top-0.5 h-5 w-5 opacity-40" />
                <div
                  className={clsx(
                    'absolute -right-2 top-0 flex h-6 w-6 items-center justify-center rounded-full',
                    stylesBydomain.COMMONS_WIDGETS_SEARCH_SUBMIT,
                    'commons_widgets_search_submit',
                  )}
                >
                  <IconArrowRight className="size-4" />
                </div>
              </div>
              <Transition
                as={Fragment}
                show={isDropdownOpen}
                enter="transform transition ease-out duration-100 mobile:transition-none"
                enterFrom="transform scale-y-0 origin-top opacity-0"
                enterTo="transform scale-y-1 origin-top opacity-100"
                leave="transform transition ease-in duration-100 mobile:transition-none"
                leaveFrom="transform scale-y-1 origin-top opacity-100"
                leaveTo="transform scale-y-0 origin-top opacity-0"
              >
                <ComboboxOptions
                  ref={comboOptionsRef}
                  static={true}
                  data-testid="search-dropdown"
                  className={clsx(
                    'absolute -left-1 z-50 mt-6 max-h-max overflow-auto rounded-2xl bg-white py-8 text-base shadow-lg ring-1 ring-black/5 focus:outline-none',
                    standalone
                      ? '-left-0 w-full mobile:mt-4'
                      : 'w-[120%] mobile:fixed mobile:left-0 mobile:mt-5 mobile:h-screen mobile:w-screen mobile:rounded-none',
                    featureFlags?.showNewHeaderAndCookieUi ? 'mobile:top-7' : 'mobile:top-11',
                  )}
                >
                  {renderCategories(fetcherCategoriesData)}
                </ComboboxOptions>
              </Transition>
            </div>
          </Combobox>
        </fetcher.Form>
      </div>
      <div
        className={clsx(
          'ml-auto flex items-center justify-center desktop:hidden',
          standalone && 'hidden',
          stylesBydomain.COMMONS_APPBAR_TEXT,
          'commons_appbar_text',
        )}
      >
        {open ? (
          <button
            type="button"
            className="mr-3 bg-inherit text-sm"
            onClick={() => setOpen(false)}
            data-testid="close-search-mobile"
            aria-label="Cancel search"
          >
            {translations.CategorySearchCombo.cancel}
          </button>
        ) : (
          <button
            type="button"
            className="z-40 flex h-5 w-5 items-center bg-inherit align-middle"
            onClick={() => setOpen(true)}
            aria-label="Open search"
            data-testid="open-search-mobile"
          >
            <BrgIconSearch className={searchIconStyle} />
          </button>
        )}
      </div>
    </div>
  )
}

export default SearchBoxRedesign
